import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Table,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import {
  IAccessControlUserListProps,
  IAccessControlUsersNormalized,
} from "../../../interfaces/Index";
import { _ } from "../../../utils/lib";
import "./AccessControlList.scss";
import searchIcon from "../../../assets/images/search-icon.svg";
import noSearchIcon from "../../../assets/no-search-found.svg";
import closeIcon from "../../../assets/images/close.svg";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import ButtonComponent from "../../common/Button/ButtonComponent";
import { AbilityContext, Can } from "../../../config/Can";
import { user } from "../../../config/constants";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchAccessControlUsers: any
) => {
  if (val.length >= 5) {
    pageScrollPosition.current = 0;
    fetchAccessControlUsers({
      searchKey: val,
    });
  }
};

const AccessControlList: React.FunctionComponent<
  IAccessControlUserListProps
> = (props: IAccessControlUserListProps) => {
  const ability = useContext(AbilityContext);

  let pageScrollPosition: any = useRef(props.currentListPosition);

  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [accessControlUserData, setData] = useState<
    Array<IAccessControlUsersNormalized>
  >(props.testAccessControlUserStateData || []);
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    const {
      // fetchAccessControlUsers,
      accessControlUsers,
      isAccessControlUserAddedorUpdated,
    } = props;
    if (!accessControlUsers || isAccessControlUserAddedorUpdated) {
      // fetchAccessControlUsers();
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, []);

  useEffect(() => {
    const { accessControlUsers, pagination } = props;
    if (accessControlUsers) {
      setData(accessControlUsers);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (!pagination) {
        setLoadMore(false);
      }
    }
  }, [props.accessControlUsers]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (props.isAccessControlUserUpdated || props.isAccessControlUserAdded) {
      const message = props.isAccessControlUserAdded
        ? "AccessControlUser added successfully!"
        : "AccessControlUser updated successfully";
      handleToaster(false, message);
    }
  }, [props.isAccessControlUserAdded, props.isAccessControlUserUpdated]);

  useEffect(() => {
    if (props.passwordReset === "passwordResetSuccess") {
      const message = props.passwordReset && "Password sent via SMS!";
      handleToaster(false, message);
    } else if (props.passwordReset === "passwordResetUnsuccessful") {
      const message = props.passwordReset && "Reset password failed!";
      handleToaster(true, message);
    }
  }, [props.passwordReset]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any) =>
        doAPIsearch(value, pageScrollPosition, props.fetchAccessControlUsers),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    } else if (value.length === 0) {
      setIsSearching(false);
      setSearchTerm("");
      closeSearch();
    }
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { fetchAccessControlUsers } = props;
    fetchAccessControlUsers();
    setIsSearching(false);
    setSearchTerm("");
  };

  const editAccessControlUser = (e: any, index: number) => {
    const { addUpdatePageHandler, userInfo } = props;
    // clears all selected accessControlUsers
    let allowUserEdit = false;
    if (userInfo.id === accessControlUserData[index].id) {
      allowUserEdit = ability.can(
        "read",
        new user({ userId: accessControlUserData[index].id })
      );
    } else {
      allowUserEdit = ability.can("read", "user");
    }
    allowUserEdit &&
      addUpdatePageHandler(
        true,
        accessControlUserData[index],
        searchTerm,
        window.pageYOffset
      );
  };

  const renderRoles = (e: any) => {
    return e.map((item: any) => (
      <span className="roles">
        {item.title}
        <i>,</i>{" "}
      </span>
    ));
  };

  const renderAccessControlList = () => {
    return (accessControlUserData! as Array<IAccessControlUsersNormalized>).map(
      (item, i) => {
        return (
          <tr key={i} onClick={(e) => editAccessControlUser(e, i)}>
            <td title={item.phoneNumber}>{item.phoneNumber}</td>
            <td title={item.fullName || "-"}>{item.fullName || "-"}</td>
            <td title={item.email || "-"}>{item.email || "-"}</td>
            <td>{renderRoles(item.roles)}</td>
            <td>
              <span
                className={
                  item.status !== "ENABLED"
                    ? "deactivated-tag"
                    : "activated-tag"
                }
              >
                {item.status !== "ENABLED" ? item.status : "active"}
              </span>
            </td>
          </tr>
        );
      }
    );
  };

  return (
    <div className="access-control-data-table-container">
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-9">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={searchIcon} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by name or phone number"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={closeIcon} alt="close" />
              </div>
            </InputGroup>
          </section>
          <section className="col-3">
            {/* <Can I="create" a="access"> */}
            <ButtonComponent
              buttonText="Add User"
              onClickHandler={() =>
                props.addUpdatePageHandler(
                  false,
                  null,
                  searchTerm,
                  window.pageYOffset
                )
              }
            />
            {/* </Can> */}
          </section>
        </section>
      </section>
      <Can I="read" a="access">
        <Table
          borderless
          striped
          className="table"
          id="access-control-user-list"
        >
          <thead>
            <tr className="table-header-row">
              <th>Phone number</th>
              <th>Full name</th>
              <th>Email</th>
              <th>Role</th>
              <th></th>
            </tr>
          </thead>
          {accessControlUserData && <tbody>{renderAccessControlList()}</tbody>}
          {props.isLoading && <LoaderComponent />}
          {isSearch &&
          accessControlUserData &&
          accessControlUserData.length === 0 ? (
            <tfoot>
              <tr>
                <div className="no-search-found">
                  <img src={noSearchIcon} alt="no search found" />
                  <div className="no-search-title">Not found</div>
                  <div className="no-search-sub-title">
                    There’s no user by that name. Did you double check?
                  </div>
                </div>
              </tr>
            </tfoot>
          ) : (
            ""
          )}
        </Table>
        {}
      </Can>
    </div>
  );
};

export default AccessControlList;
